import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eye, Loader2 } from 'lucide-react';
import { automationService, type Scenario } from '@/services/strategyService';
import { ShareScenarioDialog } from './ShareScenarioDialog';
import { ScenarioBrowser } from './ScenarioBrowser';
import { ScenarioDetail } from './ScenarioDetail';
import { readHashQuery, writeHashQuery } from './tradeViewerUtils';

/**
 * Trade Viewer — a two-level drill-down:
 *
 *   1. Scenario browser: cards, each summarising one backtest run
 *      (net P&L, win rate, PF, avg R, max DD, window). Search / sort /
 *      filter run server-side via GET /api/Scenario/page.
 *   2. Scenario detail: stat tiles + filterable trade list; picking a trade
 *      charts it against the bars the engine consumed.
 *
 * URL contract (unchanged, so old links keep working):
 *   #/dashboard/trade-viewer                       → browser
 *   #/dashboard/trade-viewer?scenario=<id>         → detail
 *   #/dashboard/trade-viewer?scenario=<id>&trade=N → detail, trade N selected
 *   #/dashboard/trade-viewer?key=<token>           → shared read-only detail
 */
export const TradeViewerPage: React.FC = () => {
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedTradeIdx, setSelectedTradeIdx] = useState(-1);
  const [shareOpen, setShareOpen] = useState(false);
  // Shared read-only mode: the page was opened via ?key=<token>. The viewer may
  // not own the scenario, so the owner-scoped browser is skipped entirely.
  const [sharedMode, setSharedMode] = useState(false);

  const openScenario = useCallback(async (scenarioId: string, tradeIdx = -1) => {
    setLoading(true);
    setLoadError(null);
    writeHashQuery({ scenario: scenarioId, trade: tradeIdx >= 0 ? String(tradeIdx) : null });
    try {
      const s = await automationService.getScenario(scenarioId);
      setScenario(s);
      setSelectedTradeIdx(tradeIdx);
    } catch (err: any) {
      setLoadError(err?.message || String(err));
      setScenario(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount: ?key=<token> → shared read-only detail; ?scenario=<id> → detail;
  // otherwise the card browser.
  useEffect(() => {
    (async () => {
      const params = readHashQuery();
      const key = params.get('key');
      const rawIdx = params.get('trade');
      const tradeIdx = rawIdx != null && !isNaN(parseInt(rawIdx, 10)) ? parseInt(rawIdx, 10) : -1;

      if (key) {
        setSharedMode(true);
        setLoading(true);
        try {
          const s = await automationService.getScenarioByShare(key);
          setScenario(s);
          setSelectedTradeIdx(tradeIdx);
        } catch {
          setLoadError('This share link is invalid or has expired.');
        } finally {
          setLoading(false);
        }
        return;
      }

      const sid = params.get('scenario');
      if (sid) await openScenario(sid, tradeIdx);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectTrade = (idx: number) => {
    setSelectedTradeIdx(idx);
    writeHashQuery({ trade: idx >= 0 ? String(idx) : null });
  };

  const backToBrowser = () => {
    setScenario(null);
    setSelectedTradeIdx(-1);
    setLoadError(null);
    writeHashQuery({ scenario: null, trade: null });
  };

  return (
    <div className="space-y-3 p-5">
      {/* Page heading — only on the browser level; the detail view has its own
          header with the back button, so this would just be noise there. */}
      {!scenario && !sharedMode && (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Eye className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Trade Viewer</h2>
            <span className="text-xs text-muted-foreground">
              Pick a backtest scenario, then inspect its trades against the bar data the engine consumed
            </span>
          </div>
        </div>
      )}

      {loadError && (
        <Card className="p-4 text-sm text-red-500 flex items-center gap-3">
          <span>{loadError}</span>
          {!sharedMode && (
            <Button variant="outline" size="sm" onClick={backToBrowser}>Back to scenarios</Button>
          )}
        </Card>
      )}

      {loading && !scenario && (
        <Card className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading scenario…
        </Card>
      )}

      {scenario ? (
        <>
          <ScenarioDetail
            scenario={scenario}
            sharedMode={sharedMode}
            onBack={backToBrowser}
            onShare={() => setShareOpen(true)}
            selectedTradeIdx={selectedTradeIdx}
            onSelectTrade={selectTrade}
          />
          {!sharedMode && (
            <ShareScenarioDialog
              runId={scenario.id}
              scenarioName={scenario.name}
              open={shareOpen}
              onOpenChange={setShareOpen}
            />
          )}
        </>
      ) : (
        !sharedMode && !loading && <ScenarioBrowser onOpen={(id) => openScenario(id)} />
      )}
    </div>
  );
};

export default TradeViewerPage;
